import { ChatMessage, ChatThreadSummary, ThinkingSegment, formatThreadTitle } from "../datastore/ChatDataStore";

// Re-exported so existing importers (FmsChatStateManager) keep their
// `import { ThinkingSegment } from "./FmsChatDisplayConverter"` path.
export type { ThinkingSegment };

// Friendly progress labels for the graph's node names. Unknown nodes fall
// back to a generic label so new graph nodes degrade gracefully.
const NODE_STATUS_LABELS: { [node: string]: string } = {
    input_adapter: "Reading your question...",
    build_prompt: "Preparing context...",
    call_llm: "Thinking...",
    validate_sql: "Validating SQL...",
    execute_query: "Running query...",
    append_exploration_result: "Exploring the data...",
    summarize: "Summarizing results...",
    render_response: "Finishing up..."
};

// Sentinel phase set the instant a send starts, before the first node update.
export const SENDING_PHASE = "sending";

// Deep link to a chat thread. Pure and React-free; the origin is passed in
// (window.location.origin at the call site) so this stays trivially testable.
export function buildThreadDeepLink(origin: string, threadId: string): string {
    return `${origin}/chat/${threadId}`;
}

// Display labels for the streamed thinking steps. Unknown steps degrade to a
// generic label so a new graph step does not render a blank prefix.
const THINKING_STEP_LABELS: { [step: string]: string } = {
    planning: "Planning",
    exploring: "Exploring",
    summarizing: "Summarizing"
};

export interface FmsChatThreadRowView {
    id: string;
    title: string;
    timestamp: string;
    // "Shared by {name}" for a thread owned by someone else; null for the
    // user's own threads. Lets the sidebar caption a shared-with-me row.
    sharedByLabel: string | null;
}

export interface CsvDownloadView {
    filename: string;
    rowNote: string | null;
}

// Pure display converter (see CLAUDE.md "Front-end Architecture Standards").
// No React imports and no constructor deps — chat formatting is context-free
// (the Eastern-time display rule is constant per the repo date convention).
export class FmsChatDisplayConverter {
    // The heading mirrors the active conversation's sidebar title, derived
    // from the open transcript so a brand-new conversation is titled by its
    // optimistic first question immediately.
    public conversationTitle(messages: ChatMessage[]): string {
        const firstQuestion = messages.find(m => m.role === "human");
        return firstQuestion ? formatThreadTitle(firstQuestion.content) : "New conversation";
    }

    // phase is the raw node name, the SENDING_PHASE sentinel, or null (idle).
    public nodeStatusLabel(phase: string | null): string | null {
        if (phase === null) {
            return null;
        }
        if (phase === SENDING_PHASE) {
            return "Sending...";
        }
        return NODE_STATUS_LABELS[phase] ?? "Working...";
    }

    public thinkingStepLabel(step: string): string {
        return THINKING_STEP_LABELS[step] ?? "Thinking";
    }

    // Pure: transcript -> per-message Thoughts, keyed by message index. Used to
    // hydrate the manager's thinkingByMsgIdx on load so reopened threads show
    // the persisted reasoning. Only ai messages that carry thinking appear.
    public thinkingByMsgIdx(messages: ChatMessage[]): { [idx: number]: ThinkingSegment[] } {
        const out: { [idx: number]: ThinkingSegment[] } = {};
        messages.forEach((m, i) => {
            if (m.role === "ai" && m.thinking && m.thinking.length > 0) {
                out[i] = m.thinking;
            }
        });
        return out;
    }

    // Pure: transcript -> per-message CSV download view-values, keyed by message
    // index. Only ai messages carrying a non-empty resultTable appear (the
    // answers with a downloadable table). `now` is injectable for tests; the
    // Eastern timestamp follows the repo date convention.
    public csvDownloadByMsgIdx(messages: ChatMessage[], now: Date = new Date()): { [idx: number]: CsvDownloadView } {
        const out: { [idx: number]: CsvDownloadView } = {};
        const stem = this.csvFilenameStem(messages, now);
        messages.forEach((m, i) => {
            if (m.role === "ai" && m.resultTable && m.resultTable.rows.length > 0) {
                const rt = m.resultTable;
                out[i] = {
                    filename: `${stem}${rt.truncated ? "-partial" : ""}.csv`,
                    rowNote: rt.truncated
                        ? `first ${rt.rows.length.toLocaleString("en-US")} of ${rt.totalRows.toLocaleString("en-US")} rows`
                        : null
                };
            }
        });
        return out;
    }

    // Slug from the conversation title; Eastern timestamp per the repo date convention.
    private csvFilenameStem(messages: ChatMessage[], now: Date): string {
        const TZ = "America/New_York";
        const slug = this.conversationTitle(messages)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 50)
            .replace(/-+$/g, "") || "table";
        const ymd = now.toLocaleDateString("en-CA", { timeZone: TZ });
        const hm = now
            .toLocaleTimeString("en-GB", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false })
            .replace(":", "");
        return `fms-${slug}-${ymd}-${hm}`;
    }

    // Friendly date + time for the thread list: "Today" / "Yesterday" for the
    // last two Eastern calendar days, otherwise a long date like "June 3, 2026",
    // each followed by the Eastern time, e.g. "Today, 9:00 AM". `now` is
    // injectable so the comparison is testable. Eastern per the repo's date
    // convention; yesterday is derived with UTC date math to dodge DST.
    public friendlyDate(iso: string, now: Date = new Date()): string {
        const TZ = "America/New_York";
        const target = new Date(iso);
        const time = target.toLocaleTimeString("en-US", { timeZone: TZ, hour: "numeric", minute: "2-digit" });
        const easternYmd = (d: Date): string => d.toLocaleDateString("en-CA", { timeZone: TZ });
        const targetYmd = easternYmd(target);
        const todayYmd = easternYmd(now);
        let day: string;
        if (targetYmd === todayYmd) {
            day = "Today";
        } else {
            const [y, m, d] = todayYmd.split("-").map(Number);
            const yesterday = new Date(Date.UTC(y, m - 1, d) - 86_400_000);
            const pad = (n: number): string => String(n).padStart(2, "0");
            const yesterdayYmd = `${yesterday.getUTCFullYear()}-${pad(yesterday.getUTCMonth() + 1)}-${pad(yesterday.getUTCDate())}`;
            day = targetYmd === yesterdayYmd
                ? "Yesterday"
                : target.toLocaleDateString("en-US", { timeZone: TZ, month: "long", day: "numeric", year: "numeric" });
        }
        return `${day}, ${time}`;
    }

    // Shared WITH me = owned by someone else. A null owner (legacy or the
    // optimistic just-created row) counts as mine, not "Shared with me".
    public isSharedWithMe(thread: ChatThreadSummary, myStytchId: string | null): boolean {
        return thread.ownerId !== null && thread.ownerId !== myStytchId;
    }

    // "Shared by {name}" for a thread owned by someone else, else null. Falls
    // back to a generic label when the owner is not in the resolved name map.
    public sharedByLabel(
        thread: ChatThreadSummary,
        myStytchId: string | null,
        nameByStytchId: { [stytchId: string]: string }
    ): string | null {
        if (!this.isSharedWithMe(thread, myStytchId)) {
            return null;
        }
        const name = thread.ownerId ? nameByStytchId[thread.ownerId] : undefined;
        return name ? `Shared by ${name}` : "Shared with you";
    }

    // Everyone except the owner and the current user. Excluding myStytchId (not
    // just ownerId) matters for a legacy null-owner thread the caller owns -
    // otherwise the owner would show as removable.
    public removableMemberIds(memberIds: string[], ownerId: string | null, myStytchId: string | null): string[] {
        return memberIds.filter(id => id !== ownerId && id !== myStytchId);
    }

    // The base row carries sharedByLabel: null; the state manager overlays the
    // real label (it holds the current user's id and the resolved name map).
    public threadRow(thread: ChatThreadSummary, now: Date = new Date()): FmsChatThreadRowView {
        return {
            id: thread.threadId,
            title: thread.title ?? "Untitled",
            timestamp: this.friendlyDate(thread.createdAt, now),
            sharedByLabel: null
        };
    }
}

export const fmsChatDisplayConverter = new FmsChatDisplayConverter();
